/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Share,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import QRCodeGenerator from 'qrcode';
import * as Clipboard from 'expo-clipboard';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { showSuccess, showError } from '../../../shared/ui/appToast';
import { storefrontPublicUrl, menuPublicUrl } from '../../domain/storeProfileTypes';

type Props = {
  slug: string;
  branchName: string;
  portalBaseUrl: string;
  /** Desktop: card centrada compacta. */
  compact?: boolean;
  /** #904: 'menu' genera QR para /m/<slug> (read-only catálogo presencial). */
  variant?: 'store' | 'menu';
};

export function QrGeneratorSection({ slug, branchName, portalBaseUrl, compact, variant = 'store' }: Props) {
  const [generating, setGenerating] = useState(false);
  const isMenu = variant === 'menu';
  const storeUrl = isMenu
    ? menuPublicUrl(portalBaseUrl, slug || 'tu-slug')
    : storefrontPublicUrl(portalBaseUrl, slug || 'tu-slug');
  const cardTitle = isMenu ? 'Código QR de tu menú' : 'Código QR de tu tienda';
  const pdfCaption = isMenu ? 'Escaneá para ver el menú' : 'Escaneá para ver el catálogo';
  const pdfDialogTitle = isMenu ? 'QR PDF de tu menú' : 'QR PDF de tu tienda';
  const pngDialogTitle = isMenu ? 'Código QR de tu menú' : 'Código QR de tu tienda';
  const emptyCaption = isMenu
    ? 'Guardá el perfil con un link válido para armar el QR del menú.'
    : 'Guardá el perfil con un link válido para armar el QR.';
  const compactCaption = isMenu
    ? 'Para mostrar en mesas o mostrador.'
    : 'Para mostrar en mostrador o vidriera.';
  const fullCaption = isMenu
    ? 'Para mostrar en mesas o mostrador.'
    : 'Para mostrar en mostrador o vidriera.';

  const handleSharePng = useCallback(async () => {
    if (!slug) return;
    setGenerating(true);
    try {
      const dataUrl = await QRCodeGenerator.toDataURL(storeUrl, {
        width: 512,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      const base64 = dataUrl.split(',')[1];
      const fileUri = `${FileSystem.cacheDirectory}qr-${slug}.png`;
      await FileSystem.writeAsStringAsync(fileUri, base64, {
        encoding: FileSystem.EncodingType.Base64,
      });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri, {
          mimeType: 'image/png',
          dialogTitle: pngDialogTitle,
        });
      } else {
        await Share.share({ url: fileUri, message: `Tienda: ${storeUrl}` });
      }
    } catch (e: any) {
      console.warn('[QR] PNG share error', e);
    } finally {
      setGenerating(false);
    }
  }, [slug, storeUrl]);

  const handleSharePdf = useCallback(async () => {
    if (!slug) return;
    setGenerating(true);
    try {
      const { jsPDF } = await import('jspdf');
      const dataUrl = await QRCodeGenerator.toDataURL(storeUrl, {
        width: 512,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      const base64 = dataUrl.split(',')[1];
      const pdf = new jsPDF({ unit: 'mm', format: 'a6' });
      pdf.setFontSize(16);
      pdf.text(branchName || 'Mi tienda', 10, 15);
      pdf.setFontSize(10);
      pdf.text(storeUrl, 10, 22);
      pdf.addImage(`data:image/png;base64,${base64}`, 'PNG', 30, 30, 80, 80);
      pdf.setFontSize(8);
      pdf.text(pdfCaption, 10, 120);
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      const fileUri = `${FileSystem.cacheDirectory}qr-${slug}.pdf`;
      await FileSystem.writeAsStringAsync(fileUri, pdfBase64, {
        encoding: FileSystem.EncodingType.Base64,
      });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri, {
          mimeType: 'application/pdf',
          dialogTitle: pdfDialogTitle,
        });
      } else {
        await Share.share({ message: `Tienda: ${storeUrl}` });
      }
    } catch (e: any) {
      console.warn('[QR] PDF share error', e);
    } finally {
      setGenerating(false);
    }
  }, [slug, storeUrl, branchName]);

  const handleCopyLink = useCallback(async () => {
    try {
      await Clipboard.setStringAsync(storeUrl);
      showSuccess('Link copiado', 'Pegalo en WhatsApp, Instagram o donde quieras.');
    } catch {
      showError('No se pudo copiar', 'Seleccioná el link y copialo a mano.');
    }
  }, [storeUrl]);

  if (!slug) {
    return (
      <PaperSheet padded>
        <Text style={styles.cardTitle}>{cardTitle}</Text>
        <Text style={styles.caption}>
          {emptyCaption}
        </Text>
      </PaperSheet>
    );
  }

  return (
    <PaperSheet padded>
      <Text style={styles.cardTitle}>{cardTitle}</Text>
      <Text style={[styles.captionBlock, compact && local.leftAlign]}>
        {compact ? compactCaption : fullCaption}
      </Text>

      <View style={local.qrContainer}>
        {generating ? (
          <ActivityIndicator color={ticketColors.ink2} />
        ) : (
          <QRCode value={storeUrl} size={200} color="black" backgroundColor="white" logoSize={0} />
        )}
      </View>

      <Text style={local.storeUrl} selectable>
        {storeUrl}
      </Text>

      <View style={[local.actions, compact && local.actionsCenter]}>
        <TouchableOpacity
          style={local.compactBtn}
          onPress={() => void handleCopyLink()}
          disabled={generating}
          accessibilityRole="button"
        >
          <Text style={local.compactBtnText}>Copiar link</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={local.compactBtn}
          onPress={() => void handleSharePng()}
          disabled={generating}
          accessibilityRole="button"
        >
          <Text style={local.compactBtnText}>Descargar PNG</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[local.compactBtn, local.compactBtnPrimary]}
          onPress={() => void handleSharePdf()}
          disabled={generating}
          accessibilityRole="button"
        >
          <Text style={[local.compactBtnText, local.compactBtnTextPrimary]}>Descargar PDF</Text>
        </TouchableOpacity>
      </View>
    </PaperSheet>
  );
}

const local = StyleSheet.create({
  compactCard: {
    width: '100%',
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  leftAlign: {
    alignSelf: 'stretch',
    textAlign: 'left',
  },
  qrContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    marginVertical: spacing.sm,
    width: '100%',
  },
  storeUrl: {
    fontSize: 12,
    fontFamily: ticketFonts.mono ?? ticketFonts.sans,
    color: ticketColors.ink2,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignSelf: 'stretch',
  },
  actionsCenter: {
    justifyContent: 'center',
  },
  compactBtn: {
    alignSelf: 'flex-start',
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 8,
    paddingHorizontal: 12,
    minHeight: 36,
    justifyContent: 'center',
  },
  compactBtnPrimary: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  compactBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  compactBtnTextPrimary: {
    color: ticketColors.goldInk,
  },
});
